import { srgbChannelToLinear } from './accent-theme'

// Extracted from ColorField so the WCAG math is testable without pulling React in.

export type ContrastTextColor = 'white' | 'black'

export type Rgb = { r: number; g: number; b: number }

export function parseHexColor(hex: string): Rgb | null {
  const v = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null
  return {
    r: Number.parseInt(v.slice(0, 2), 16),
    g: Number.parseInt(v.slice(2, 4), 16),
    b: Number.parseInt(v.slice(4, 6), 16),
  }
}

export function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b)
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

type Hsl = { h: number; s: number; l: number }

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const R = r / 255
  const G = g / 255
  const B = b / 255
  const max = Math.max(R, G, B)
  const min = Math.min(R, G, B)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === R) h = (G - B) / d + (G < B ? 6 : 0)
    else if (max === G) h = (B - R) / d + 2
    else h = (R - G) / d + 4
    h /= 6
  }
  return { h, s, l }
}

const hue2rgb = (p: number, q: number, t: number) => {
  let tt = t
  if (tt < 0) tt += 1
  if (tt > 1) tt -= 1
  if (tt < 1 / 6) return p + (q - p) * 6 * tt
  if (tt < 1 / 2) return q
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
  return p
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  if (s === 0) {
    const v = Math.round(l * 255)
    return { r: v, g: v, b: v }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  }
}

const pad = (n: number) => n.toString(16).padStart(2, '0')
const rgbToHex = ({ r, g, b }: Rgb) => `#${pad(r)}${pad(g)}${pad(b)}`

// Binary search on lightness; null when it already passes or no shade of this hue can reach the target.
export function findCompliantColor(brand: Rgb, textColor: ContrastTextColor, targetRatio: number): string | null {
  const textRgb = textColor === 'white' ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 }
  if (contrastRatio(brand, textRgb) >= targetRatio) return null

  const { h, s, l: originalL } = rgbToHsl(brand)
  let goodL = textColor === 'white' ? 0 : 1
  let badL = originalL
  for (let i = 0; i < 20; i++) {
    const midL = (goodL + badL) / 2
    if (contrastRatio(hslToRgb({ h, s, l: midL }), textRgb) >= targetRatio) goodL = midL
    else badL = midL
  }
  const candidate = hslToRgb({ h, s, l: goodL })
  return contrastRatio(candidate, textRgb) >= targetRatio ? rgbToHex(candidate) : null
}
